import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styles from './ContractsScreen.module.scss';
import Popover from 'common/components/popover/Popover';
import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import FiltersIcon from 'assets/svg/desktop_filtres.svg';
import ImportIcon from 'assets/svg/file-arrow-up-solid.svg';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import ReactTooltip from 'react-tooltip';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE, DATE_FORMAT_DEFAUT } from 'Config';
import dayjs from 'dayjs';
import ContractsFiltersScreen, { ContractsFilters } from './components/ContractsFiltersScreen';
import FilterBadgesOverflow, { FilterBadgeItem } from 'common/components/filterBadgesOverflow/FilterBadgesOverflow';
import { CompactPageLayout, compactPageLayoutStyles } from 'common/components/compactPageLayout';
import ExportListingButton from 'common/components/exportListingButton/ExportListingButton';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import ContractsListScreen from './components/ContractsListScreen';
import { UnifiedContractsSearchCriteria } from 'api/contracts/unifiedListing/models/UnifiedContractsSearchCriteria';
import VatNotice from 'common/components/vatNotice/VatNotice';

const FILTERS_CACHE_KEY = 'contracts-listing-filters';

const ContractsScreen = () => {
    const history = useHistory();
    const { t } = useTranslation();
    const isFirstLoad = useRef<boolean>(true);
    const exportListingRef = useRef<{ exportExcel: () => Promise<void> } | null>(null);

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasContractsWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_CONTRACTS_WRITE']);

    const [filters, setFilters] = useState<ContractsFilters>({ showOnlyLast: true });
    const [filtersTotal, setFiltersTotal] = useState(0);

    const [criteria, setCriteria] = useState<UnifiedContractsSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderColumn: 'c.start_date',
        orderBy: 'desc',
    });

    const updateTotalFilters = (f: ContractsFilters) => {
        let total = 0;
        if (f.contractType) total++;
        if (f.state) total++;
        if (f.vehicleId) total++;
        if (f.contractDateFrom || f.contractDateTo) total++;
        if (f.brandId) total++;
        if (f.modelId) total++;
        if (f.supplierId) total++;
        if (f.segmentId) total++;
        if (f.localIds?.length) total++;
        if (f.hasReminder !== undefined) total++;
        if (f.costMin !== undefined || f.costMax !== undefined) total++;
        if (f.vehiclesInactive) total++;
        if (f.showOnlyLast) total++;
        setFiltersTotal(total);
    };

    const loadFilters = () => {
        const cachedFilters = localStorage.getItem(FILTERS_CACHE_KEY);
        if (cachedFilters != null) {
            onChangeFilters(JSON.parse(cachedFilters), true);
        } else {
            onChangeFilters({ showOnlyLast: true }, true);
        }
    };

    const onChangeFilters = (_filters: ContractsFilters, isOnLoad?: boolean) => {
        updateTotalFilters(_filters);
        setFilters({ ..._filters });
        localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(_filters));

        setCriteria(prev => ({
            ...prev,
            contractType: _filters.contractType,
            subTypeId: _filters.contractTypeOptionValue ? _filters.contractTypeOptionValue.split(':')[1] : undefined,
            state: _filters.state,
            vehicleId: _filters.vehicleId,
            contractDateFrom: _filters.contractDateFrom,
            contractDateTo: _filters.contractDateTo ? new Date(new Date(_filters.contractDateTo).setHours(23, 59, 59)) : undefined,
            brandId: _filters.brandId,
            modelId: _filters.modelId,
            supplierId: _filters.supplierId,
            segmentId: _filters.segmentId,
            localIds: _filters.localIds,
            localOperator: _filters.localOperator,
            hasReminder: _filters.hasReminder,
            costMin: _filters.costMin,
            costMax: _filters.costMax,
            vehiclesInactive: _filters.vehiclesInactive,
            showOnlyLast: _filters.showOnlyLast,
            orderBy: _filters.orderBy || prev.orderBy,
            orderColumn: _filters.orderColumn || prev.orderColumn,
            page: (isOnLoad && _filters.page) ? _filters.page : 1,
        }));

        isFirstLoad.current = false;
    };

    const onRowClick = useCallback((vehicleId: string, contractType: string, contractId: string) => {
        const type = contractType.toLowerCase();
        history.push(`/vehicles/details/${vehicleId}/contract/details/${type}/details/${contractId}`);
    }, [history]);

    useEffect(() => {
        loadFilters();
    }, []);

    const filterItems = useMemo<FilterBadgeItem[]>(() => {
        const items: FilterBadgeItem[] = [];

        if (filters.contractType && filters.contractTypeName) {
            items.push({
                key: 'contractType',
                label: t('contracts_listing.filters.contract_type') as string,
                value: filters.contractTypeName,
                onRemove: () => onChangeFilters({ ...filters, contractType: undefined, contractTypeName: undefined, contractTypeOptionValue: undefined })
            });
        }

        if (filters.state && filters.stateName) {
            items.push({
                key: 'state',
                label: t('contracts_listing.filters.state') as string,
                value: filters.stateName,
                onRemove: () => onChangeFilters({ ...filters, state: undefined, stateName: undefined })
            });
        }

        if (filters.vehicleId && filters.vehicleName) {
            items.push({
                key: 'vehicleId',
                label: t('vehicle.vehicle') as string,
                value: filters.vehicleName,
                onRemove: () => onChangeFilters({ ...filters, vehicleId: undefined, vehicleName: undefined })
            });
        }

        if (filters.contractDateFrom || filters.contractDateTo) {
            const start = filters.contractDateFrom ? dayjs(filters.contractDateFrom).format(DATE_FORMAT_DEFAUT) : '';
            const end = filters.contractDateTo ? dayjs(filters.contractDateTo).format(DATE_FORMAT_DEFAUT) : '';
            const value = start && end
                ? `${t('common.range.from')} ${start} ${t('common.range.to').toLowerCase()} ${end}`
                : start
                    ? `${t('common.range.from')} ${start}`
                    : `${t('common.range.to')} ${end}`;
            items.push({
                key: 'contractDateRange',
                label: t('contracts_listing.filters.contract_date_range' as any) as string,
                value,
                onRemove: () => onChangeFilters({ ...filters, contractDateFrom: undefined, contractDateTo: undefined })
            });
        }

        if (filters.brandId && filters.brandName) {
            items.push({
                key: 'brandId',
                label: t('contracts_listing.filters.brand') as string,
                value: filters.brandName,
                onRemove: () => onChangeFilters({ ...filters, brandId: undefined, brandName: undefined })
            });
        }

        if (filters.modelId && filters.modelName) {
            items.push({
                key: 'modelId',
                label: t('contracts_listing.filters.model') as string,
                value: filters.modelName,
                onRemove: () => onChangeFilters({ ...filters, modelId: undefined, modelName: undefined })
            });
        }

        if (filters.supplierId && filters.supplierName) {
            items.push({
                key: 'supplierId',
                label: t('contracts_listing.filters.supplier') as string,
                value: filters.supplierName,
                onRemove: () => onChangeFilters({ ...filters, supplierId: undefined, supplierName: undefined })
            });
        }

        if (filters.segmentId && filters.segmentName) {
            items.push({
                key: 'segmentId',
                label: t('vehicle.type') as string,
                value: filters.segmentName,
                onRemove: () => onChangeFilters({ ...filters, segmentId: undefined, segmentName: undefined })
            });
        }

        if (filters.localIds?.length && filters.localNames?.length) {
            items.push({
                key: 'localIds',
                label: `${t('vehicle.local')} (${filters.localOperator === 'AND' ? t('common.filter_all' as any) : t('common.filter_any' as any)})` as string,
                value: filters.localNames.join(', '),
                onRemove: () => onChangeFilters({ ...filters, localIds: [], localNames: [] })
            });
        }

        if (filters.hasReminder !== undefined) {
            items.push({
                key: 'hasReminder',
                label: t('contracts_listing.filters.has_reminder') as string,
                value: filters.hasReminder ? t('common.yes') as string : t('common.no') as string,
                onRemove: () => onChangeFilters({ ...filters, hasReminder: undefined })
            });
        }

        if (filters.costMin !== undefined || filters.costMax !== undefined) {
            const min = filters.costMin !== undefined ? `${filters.costMin}€` : '';
            const max = filters.costMax !== undefined ? `${filters.costMax}€` : '';
            items.push({
                key: 'cost',
                label: t('contracts_listing.filters.cost') as string,
                value: min && max ? `${min} - ${max}` : min || max,
                onRemove: () => onChangeFilters({ ...filters, costMin: undefined, costMax: undefined })
            });
        }

        if (filters.vehiclesInactive) {
            items.push({
                key: 'vehiclesInactive',
                label: t('contracts_listing.filters.include_inactive') as string,
                value: t('common.yes') as string,
                onRemove: () => onChangeFilters({ ...filters, vehiclesInactive: false })
            });
        }

        if (filters.showOnlyLast) {
            items.push({
                key: 'showOnlyLast',
                label: t('contracts_listing.filters.show_only_last') as string,
                value: t('common.yes') as string,
                onRemove: () => onChangeFilters({ ...filters, showOnlyLast: false })
            });
        }

        return items;
    }, [filters, t]);

    return (
        <ScreenTitle title={t('contracts_listing.title')}>
            <CompactPageLayout
                title={t('contracts_listing.title') as string}
                contentAreaClassName={styles.contentArea}
                middleContent={<FilterBadgesOverflow items={filterItems} align="right" />}
                headerButtons={
                    <>
                        <Popover
                            contentContainerClassName={styles.filtersPopoverContainer}
                            containerStyle={{ boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)', backgroundColor: '#fff', overflowY: 'visible' }}
                            onClickOutside={() => updateTotalFilters(filters)}
                            content={setIsPopoverOpen => <ContractsFiltersScreen
                                filters={filters}
                                onFilter={_filters => { setIsPopoverOpen(false); onChangeFilters(_filters) }}
                                onChange={_filters => updateTotalFilters(_filters)}
                            />}
                        >
                            {(isPopoverOpen, setIsPopoverOpen) => (
                                <ScreenHeaderButton icon={FiltersIcon} onClick={() => setIsPopoverOpen(!isPopoverOpen)}>
                                    {filtersTotal > 0 &&
                                        <div className={styles.counterList}> <div className={styles.counterNumber}>{filtersTotal}</div> </div>
                                    }
                                </ScreenHeaderButton>
                            )}
                        </Popover>
                        <ExportListingButton exportRef={exportListingRef} />
                        {hasContractsWritePolicy &&
                            <div className={styles.importButton} data-tip={t('bulk_contracts.tooltip')}>
                                <ScreenHeaderButton classNameIcon={styles.importIcon} icon={ImportIcon} onClick={() => history.push('/bulk-contracts')} />
                                <ReactTooltip />
                            </div>}
                    </>
                }
            >
                {!isFirstLoad.current && (
                    <div className={compactPageLayoutStyles.countersSection}>
                        <VatNotice />
                    </div>
                )}
                {!isFirstLoad.current &&
                    <ContractsListScreen
                        criteria={criteria}
                        onClickItem={onRowClick}
                        exportRef={exportListingRef}
                    />
                }
            </CompactPageLayout>
        </ScreenTitle>
    );
};

export default ContractsScreen;
